import { Close } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { sortAndGroupLearningPaths } from 'src/pages/LearningPaths/utils';

import {
  LearningPathCardCarousel,
  LearningPathCardSkeleton,
  LearningPathCardVariants,
} from '@/components';
import { NEW_FEATURE_ALERTS } from '@/constants';
import { useNewFeatureAlert } from '@/hooks';
import { PinFilledIcon } from '@/icons';
import { EVENT_CONSTANTS, EVENTS, logEvent } from '@/services';
import {
  selectPlan,
  selectUserCompliance,
  useFetchLearningPathsQuery,
  useFetchOrgPinnedLpsQuery,
} from '@/store';
import { appStyles } from '@/theme';

import { PinLpButton } from '../PinLpButton';
import { ReorderPinnedLpsToolbar } from '../ReorderPinnedLpsToolbar';

const REFERRER = {
  page: EVENT_CONSTANTS.PAGE.CONTENT_CURATION,
  component: EVENT_CONSTANTS.COMPONENT.LEARNING_PATH_CARD,
};

export const PinnedLpsCurator = () => {
  const compliance = useSelector(selectUserCompliance);
  const plan = useSelector(selectPlan);

  const newFeatureAlert = useNewFeatureAlert(
    NEW_FEATURE_ALERTS.PINNED_LPS_CURATOR,
  );

  const { data: orgPinnedLps } = useFetchOrgPinnedLpsQuery();
  const {
    data: learningPaths,
    isLoading,
    isUninitialized,
  } = useFetchLearningPathsQuery({
    compliance,
  });

  const lpsWithDurationLeft = useMemo(() => {
    if (!learningPaths) return [];

    return learningPaths.map(lp => ({
      ...lp,
      durationPlayed: 0,
      completions: 0,
      isCompleted: false,
      lastPositionEventAt: '',
    }));
  }, [learningPaths]);

  const groupedByCategoryLps = useMemo(() => {
    if (!lpsWithDurationLeft) return [];
    return sortAndGroupLearningPaths(lpsWithDurationLeft);
  }, [lpsWithDurationLeft]);

  const pinnedLps = useMemo(() => {
    if (!lpsWithDurationLeft || !orgPinnedLps) return [];

    return orgPinnedLps
      .map(lp => lpsWithDurationLeft.find(l => l.groupId === lp.groupId))
      .filter(lp => lp !== undefined);
  }, [lpsWithDurationLeft, orgPinnedLps]);

  return (
    <Box sx={[styles.root, appStyles.limitPageWidth]}>
      <Box>
        <Typography variant="h1" gutterBottom>
          Learning Paths
        </Typography>
        <Typography variant="body2" gutterBottom>
          Curate the learning paths page, to help your team dig deeper into
          specific skills or topics.
        </Typography>
      </Box>
      {newFeatureAlert.isVisible && (
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'primary.main',
            color: 'text.contrast',
            width: 500,
            padding: 2.5,
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
          }}>
          <IconButton
            aria-label="Dismiss new feature alert"
            onClick={() => {
              logEvent(EVENTS.CONTENT_CURATION_EVENTS.LP_MESSAGE_DISMISSED);
              newFeatureAlert.dismiss();
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}>
            <Close
              sx={{
                fontSize: 28,
                color: 'text.contrast',
              }}
            />
          </IconButton>
          <PinFilledIcon />
          <Typography variant="h4" sx={{ color: 'text.contrast' }}>
            Pin and rearrange learning paths
          </Typography>
          <Typography
            variant="body3"
            textAlign="center"
            sx={{ color: 'text.contrast' }}>
            You can now pin, unpin and rearrange learning paths in your curated
            carousel! Highlight both custom and LumiQ learning paths to create
            curated learning for your organization.
          </Typography>
        </Box>
      )}
      {isLoading || isUninitialized ? (
        <Box sx={styles.skeletonContainer}>
          {Array.from({ length: 9 }).map((_, index) => (
            <LearningPathCardSkeleton
              key={index}
              variant={LearningPathCardVariants.HORIZONTAL}
            />
          ))}
        </Box>
      ) : (
        <>
          {pinnedLps.length > 0 && (
            <LearningPathCardCarousel
              key="pinned-lps"
              title={`Curated for ${plan?.data?.organizationName}`}
              uKey="pinned-lps"
              learningPaths={pinnedLps}
              referrer={{
                ...REFERRER,
                carousel: 'pinned_lps',
              }}
              LpCardComponents={{
                SuperTitle: PinLpButton,
                Footer: ReorderPinnedLpsToolbar,
              }}
            />
          )}
          {groupedByCategoryLps.map(({ groupKey, lps }) => (
            <LearningPathCardCarousel
              key={groupKey}
              title={groupKey}
              uKey={groupKey}
              learningPaths={lps}
              referrer={REFERRER}
              LpCardComponents={{
                SuperTitle: PinLpButton,
              }}
            />
          ))}
        </>
      )}
    </Box>
  );
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: {
      xs: 4,
      md: 6,
    },
  },
  skeletonContainer: {
    display: 'flex',
    gap: 3,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    flexWrap: 'wrap',
  },
};
